import { useState, useCallback, useRef } from 'react';
import { debugError } from '../../../../utils/debugLog';
import { fetchWithDeduplication } from '../useDashboardDataUtils';

/**
 * useCoreData Hook
 * Manages core dashboard data (overview, cost breakdown)
 */
export const useCoreData = (queryString, requestKeySuffix, isAllTime) => {
  const [coreLoading, setCoreLoading] = useState(false);
  const [coreError, setCoreError] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const isMountedRef = useRef(true);

  const fetchCoreData = useCallback(async () => {
    if (!isMountedRef.current) return;

    setCoreLoading(true);
    setCoreError(null);

    try {
      let overviewResponse, costBreakdownResponse;

      if (isAllTime) {
        // For all-time: Use new static metrics endpoint (MV-based, no 200 limit)
        try {
          overviewResponse = await fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/overview/static`,
            `dashboard-static-metrics-all-time`
          );
          if (overviewResponse?.success) {
            // Ensure period is set
            overviewResponse.period = overviewResponse.period || 'All Time';
          }
        } catch (staticError) {
          debugError('Error fetching static metrics endpoint:', staticError);
          // Fallback: use legacy endpoint if static metrics fails
          overviewResponse = await fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/overview?days=30`,
            `overview-all-time-fallback`
          );
          if (overviewResponse?.success) {
            overviewResponse.period = 'All Time';
          }
        }

        // For all-time: Use execution-based cost breakdown endpoint
        try {
          const allTimeStartDate = '2000-01-01T00:00:00Z';
          const allTimeEndDate = new Date().toISOString();
          costBreakdownResponse = await fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/cost-breakdown?start_date=${encodeURIComponent(allTimeStartDate)}&end_date=${encodeURIComponent(allTimeEndDate)}`,
            `cost-breakdown-all-time`
          );
          if (costBreakdownResponse?.success) {
            costBreakdownResponse.period = 'All Time';
          }
        } catch (costError) {
          debugError('Error fetching all-time cost breakdown:', costError);
          costBreakdownResponse = {
            success: false,
            models: [],
            period: 'All Time',
            message: 'Failed to fetch cost breakdown from executions',
          };
        }
      } else {
        // Time-filtered: use legacy endpoints
        [overviewResponse, costBreakdownResponse] = await Promise.all([
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/overview?${queryString}`,
            `overview-${requestKeySuffix}`
          ),
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/cost-breakdown?${queryString}`,
            `cost-breakdown-${requestKeySuffix}`
          ),
        ]);
      }

      // Validate critical responses
      if (!overviewResponse?.success) {
        throw new Error('Failed to fetch overview data');
      }

      if (!isAllTime && !costBreakdownResponse?.success) {
        throw new Error('Failed to fetch cost breakdown data');
      }

      // Process cost breakdown data
      const costBreakdownData = {
        models: costBreakdownResponse?.models || [],
        period:
          costBreakdownResponse?.period || (isAllTime ? 'All Time' : 'N/A'),
        success: costBreakdownResponse?.success || false,
      };

      // Update core data state
      if (isMountedRef.current) {
        setOverviewData(overviewResponse);
        setCostBreakdown(costBreakdownData);
      }
    } catch (err) {
      if (isMountedRef.current) {
        if (
          err.isAuthError ||
          err.message?.includes('Authentication required')
        ) {
          setCoreError(
            `Authentication required: Please log in to view dashboard data. ${err.message || ''}`
          );
        } else {
          setCoreError(
            `Failed to load core dashboard data: ${err.message || 'Unknown error'}`
          );
        }
      }
    } finally {
      if (isMountedRef.current) {
        setCoreLoading(false);
      }
    }
  }, [queryString, requestKeySuffix, isAllTime]);

  return {
    coreLoading,
    coreError,
    overviewData,
    costBreakdown,
    setCostBreakdown,
    fetchCoreData,
    isMountedRef,
  };
};
